import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calendar, Flag, CheckSquare, User, Users, Clock, TrendingUp, AlertCircle } from "lucide-react";
import { format, differenceInDays, isPast, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";

export default function ProjectDashboard({ project, tasks, milestones, timeEntries, teamMembers, comments }) {
  // Calculate progress
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === "completed").length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // Get upcoming deadlines (next 14 days)
  const now = new Date();
  const upcomingTasks = tasks
    .filter(t => t.due_date && t.status !== "completed")
    .map(t => ({
      ...t,
      type: 'task',
      daysUntil: differenceInDays(parseISO(t.due_date), now),
    }))
    .filter(t => t.daysUntil >= -1 && t.daysUntil <= 14);

  const upcomingMilestones = milestones
    .filter(m => m.due_date && m.status !== "completed")
    .map(m => ({
      ...m,
      type: 'milestone',
      daysUntil: differenceInDays(parseISO(m.due_date), now),
    }))
    .filter(m => m.daysUntil >= -1 && m.daysUntil <= 14);

  const allUpcoming = [...upcomingTasks, ...upcomingMilestones]
    .sort((a, b) => a.daysUntil - b.daysUntil)
    .slice(0, 5);

  // Calculate time stats
  const totalHours = timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0) / 60, 0);
  const billableHours = timeEntries
    .filter(e => e.billable)
    .reduce((sum, entry) => sum + (entry.duration_minutes || 0) / 60, 0);

  // Get unique team members from tasks
  const uniqueMembers = new Set(tasks.map(t => t.assigned_to).filter(Boolean));
  const activeMembers = Array.from(uniqueMembers).map(email => {
    const assignedTasks = tasks.filter(t => t.assigned_to === email);
    const completed = assignedTasks.filter(t => t.status === "completed").length;
    return {
      email,
      name: assignedTasks[0]?.assigned_to_name || email,
      taskCount: assignedTasks.length,
      completedCount: completed,
    };
  }).slice(0, 5);

  // Recent activity from comments
  const recentComments = comments
    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
    .slice(0, 3);

  const getDaysText = (days) => {
    if (days < 0) return `${Math.abs(days)} dag${Math.abs(days) !== 1 ? 'er' : ''} forsinket`;
    if (days === 0) return "I dag";
    if (days === 1) return "I morgen";
    return `Om ${days} dager`;
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Oppgaver</p>
              <p className="text-2xl font-bold text-slate-900">{completedTasks}/{totalTasks}</p>
            </div>
          </div>
          <Progress value={progress} className="h-1.5 mt-3" />
        </Card>

        <Card className="p-4 bg-gradient-to-br from-lime-100 to-lime-100 border-lime-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center">
              <Flag className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Milepæler</p>
              <p className="text-2xl font-bold text-slate-900">
                {milestones.filter(m => m.status === "completed").length}/{milestones.length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Timer totalt</p>
              <p className="text-2xl font-bold text-slate-900">{Math.round(totalHours)}</p>
            </div>
          </div>
          <p className="text-xs text-slate-600 mt-2">{Math.round(billableHours)}t fakturerbare</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Teammedlemmer</p>
              <p className="text-2xl font-bold text-slate-900">{uniqueMembers.size}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Deadlines */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Kommende Deadlines</h3>
            {allUpcoming.filter(item => item.daysUntil < 0).length > 0 && (
              <Badge className="bg-rose-100 text-rose-700 border-0 ml-auto">
                {allUpcoming.filter(item => item.daysUntil < 0).length} forsinket
              </Badge>
            )}
          </div>

          {allUpcoming.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Ingen kommende deadlines</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allUpcoming.map((item) => {
                const isOverdue = item.daysUntil < 0;
                const Icon = item.type === 'milestone' ? Flag : CheckSquare;
                
                return (
                  <div
                    key={`${item.type}-${item.id}`}
                    className={cn(
                      "p-3 rounded-lg border",
                      isOverdue ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1">
                        <Icon className={cn("w-4 h-4 mt-0.5", isOverdue ? "text-rose-600" : "text-slate-600")} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {item.type === 'milestone' ? item.name : item.title}
                          </p>
                          {item.type === 'task' && item.assigned_to_name && (
                            <p className="text-xs text-slate-600">{item.assigned_to_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <Badge className={cn(
                          "text-xs border-0",
                          isOverdue ? "bg-rose-200 text-rose-800" : "bg-blue-100 text-blue-800"
                        )}>
                          {getDaysText(item.daysUntil)}
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">
                          {format(parseISO(item.due_date), "dd MMM", { locale: nb })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Team Overview */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Teamoversikt</h3>
          </div>

          {/* Project Owner */}
          <div className="mb-4 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
                <User className="w-5 h-5 text-zinc-900" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{project.owner_name || project.owner}</p>
                <p className="text-xs text-slate-500">Prosjekteier</p>
              </div>
            </div>
          </div>

          {/* Team Members */}
          {activeMembers.length === 0 ? (
            <div className="text-center py-6">
              <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Ingen teammedlemmer tildelt oppgaver</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeMembers.map((member) => (
                <div key={member.email} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-xs font-medium text-slate-700">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{member.name}</p>
                      <p className="text-xs text-slate-500">
                        {member.completedCount}/{member.taskCount} fullført
                      </p>
                    </div>
                  </div>
                  <div className="w-16">
                    <Progress 
                      value={(member.completedCount / member.taskCount) * 100} 
                      className="h-1.5"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Nylig Aktivitet</h3>
        </div>

        {recentComments.length === 0 ? (
          <div className="text-center py-8">
            <TrendingUp className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Ingen nylige kommentarer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentComments.map((comment) => (
              <div key={comment.id} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-slate-700">
                      {comment.user_name?.charAt(0).toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-slate-900">{comment.user_name}</p>
                      <span className="text-xs text-slate-500">
                        {format(new Date(comment.created_date), "dd.MM.yyyy HH:mm", { locale: nb })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{comment.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}